import type { LucideIcon } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type SafariBrowserFrameProps = {
  url?: string;
  children: ReactNode;
  /** Optional element(s) rendered immediately after the URL bar, inside the centered
   *  group — for actions logically tied to the URL itself (e.g. "Copy link"). */
  urlAction?: ReactNode;
  /** Optional element(s) rendered on the right side of the browser chrome (mirrors
   *  the position of the traffic-light dots on the left). */
  headerAction?: ReactNode;
  /** Hide the macOS-style traffic-light window controls. Useful in mobile preview
   *  where a phone-shaped chrome shouldn't show desktop window dots. */
  hideWindowControls?: boolean;
};

export const SafariBrowserFrame = ({
  url,
  children,
  urlAction,
  headerAction,
  hideWindowControls = false,
}: SafariBrowserFrameProps) => (
  // `@container` here so container-query rules on the BrowserHeaderButtons (or any
  // other chrome-internal responsive element) evaluate against the FRAME width.
  // Without this they'd evaluate against an outer @container (e.g. the preview
  // section) and wouldn't truncate when the user switches to mobile preview, where
  // the frame is constrained to a narrow column inside a still-wide preview section.
  <div className="@container flex flex-col w-full h-full rounded-xl overflow-hidden border border-border bg-card shadow-sm">
    <div className="flex items-center gap-2 px-4 py-3 bg-muted border-b border-border">
      {!hideWindowControls && (
        <div className="flex items-center gap-1.5">
          <div className="size-3 rounded-full bg-[#FF5F57]" />
          <div className="size-3 rounded-full bg-[#FEBC2E]" />
          <div className="size-3 rounded-full bg-[#28C840]" />
        </div>
      )}

      <div className="flex-1 flex items-center justify-center gap-1">
        <div className="flex items-center justify-center bg-card border border-border rounded-md px-3 py-1 min-w-0 w-[280px] max-w-full">
          <span className="text-xs text-foreground-subtle truncate">{url}</span>
        </div>
        {urlAction}
      </div>

      <div className="flex items-center justify-end gap-1 shrink-0">{headerAction}</div>
    </div>

    <div className="flex-1 overflow-hidden">{children}</div>
  </div>
);

type BrowserHeaderButtonProps = {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  /** Tooltip content shown on hover when the visible label is hidden (i.e. the
   *  button has collapsed to icon-only). When the label is visible the tooltip is
   *  suppressed because the button text is already self-explanatory. */
  tooltip?: ReactNode;
  'aria-label'?: string;
  'data-testid'?: string;
};

/**
 * Shared button used inside the SafariBrowserFrame's `headerAction` / `urlAction`
 * slots — and any other browser-preview chrome. Same visual treatment across all
 * consumers (WidgetSettings "Open in new tab", ChatPage "Copy link" / "Open in new
 * tab") so they read as a set, and label collapses at the same `@2xl` (42rem /
 * 672px) container-query breakpoint everywhere.
 *
 * The component self-detects when its label is hidden (via `getComputedStyle` on
 * a ref'd label span + a `ResizeObserver` on the button itself, which resizes
 * when the label appears/disappears) and shows the tooltip only then. This means
 * callers don't have to manage a separate "showLabels" sentinel — the tooltip
 * stays in lockstep with the actual CSS state regardless of which @container
 * ancestor the button ends up under.
 */
export const BrowserHeaderButton = forwardRef<HTMLButtonElement, BrowserHeaderButtonProps>(
  ({ icon: Icon, label, onClick, tooltip, ...props }, ref) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const [labelHidden, setLabelHidden] = useState(false);
    useEffect(() => {
      const buttonEl = buttonRef.current;
      const labelEl = labelRef.current;
      if (!buttonEl || !labelEl) return;
      const update = () => setLabelHidden(getComputedStyle(labelEl).display === 'none');
      update();
      const ro = new ResizeObserver(update);
      ro.observe(buttonEl);
      return () => ro.disconnect();
    }, []);

    const setRefs = (node: HTMLButtonElement | null) => {
      buttonRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    const button = (
      <button
        ref={setRefs}
        type="button"
        onClick={onClick}
        className="inline-flex items-center justify-center gap-2 h-7 px-3 rounded-md border border-border bg-background text-sm text-foreground hover:bg-accent transition-colors duration-80 cursor-pointer @max-2xl:gap-0 @max-2xl:px-2"
        {...props}
      >
        <Icon size={14} strokeWidth={1.75} />
        <span ref={labelRef} className="hidden @2xl:inline">
          {label}
        </span>
      </button>
    );
    if (tooltip == null) return button;
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          {labelHidden && <TooltipContent>{tooltip}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    );
  },
);
BrowserHeaderButton.displayName = 'BrowserHeaderButton';
